import { useState, useEffect, useRef } from "react";
import { router } from "@inertiajs/react";
import Win2kWindow from "@/components/win2k/Win2kWindow";
import Win2kButton from "@/components/win2k/Win2kButton";
import RulesModal from "@/components/win2k/RulesModal";

interface HomeProps {
  errors?: {
    username?: string[];
    code?: string[];
    room?: string[];
  };
  kicked?: boolean;
  room_code?: string;
}

export default function Home({ errors, kicked, room_code }: HomeProps) {
  const [showKickedNotice, setShowKickedNotice] = useState(kicked ?? false);
  const [username, setUsername] = useState(() => {
    try { return localStorage.getItem("flip7_username") ?? ""; } catch { return ""; }
  });
  const [roomCode, setRoomCode] = useState(() => {
    if (room_code) return room_code;
    try { return localStorage.getItem("flip7_roomCode") ?? ""; } catch { return ""; }
  });
  const [processing, setProcessing] = useState(false);
  const [showRules, setShowRules] = useState(false);
  const usernameRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (showKickedNotice) {
      const timer = setTimeout(() => setShowKickedNotice(false), 5000);
      return () => clearTimeout(timer);
    }
  }, [showKickedNotice]);

  function persistToLocalStorage() {
    try {
      localStorage.setItem("flip7_username", username);
      localStorage.setItem("flip7_roomCode", roomCode);
    } catch { /* private browsing or storage full */ }
  }

  function handleCreate(e: React.FormEvent) {
    e.preventDefault();
    if (!username.trim()) {
      usernameRef.current?.focus();
      return;
    }
    persistToLocalStorage();
    setProcessing(true);
    router.post("/rooms", { username }, {
      onFinish: () => setProcessing(false),
    });
  }

  function handleJoin(e: React.FormEvent) {
    e.preventDefault();
    if (!username.trim()) {
      usernameRef.current?.focus();
      return;
    }
    persistToLocalStorage();
    setProcessing(true);
    router.post("/rooms/join", { username, code: roomCode }, {
      onFinish: () => setProcessing(false),
    });
  }

  return (
    <div className="min-h-screen bg-[#008080] flex items-center justify-center p-4">
      {showKickedNotice && (
        <div className="fixed top-4 left-1/2 -translate-x-1/2 z-50 win2k-outset bg-[var(--win2k-window-bg)] px-4 py-2 max-w-xs">
          <p className="text-[12px] text-[var(--win2k-button-text)]">
            You were removed from the room.
          </p>
        </div>
      )}

      <Win2kWindow
        title="Flip 7 - Multiplayer Card Game"
        statusBar="Ready"
        className="max-w-md"
        menuBar={
          <button type="button" onClick={() => setShowRules(true)} className="px-1 cursor-default text-xs hover:underline">
            How to Play
          </button>
        }
      >
        <div className="space-y-4">
          <h1 className="font-pixel text-sm md:text-base text-center text-[var(--win2k-black)] leading-relaxed">
            Flip 7
          </h1>
          <p className="text-[11px] text-center text-[var(--win2k-button-text)]">
            A press-your-luck multiplayer card game
          </p>

          <label className="block text-[11px]">
            Username:
            <input
              ref={usernameRef}
              type="text"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              className="win2k-field block w-full mt-0.5 px-1 py-0.5 text-base md:text-[12px]"
              maxLength={20}
            />
          </label>
          {errors?.username && (
            <p className="text-[11px] text-red-700">
              {errors.username[0]}
            </p>
          )}

          <form onSubmit={handleJoin} className="space-y-2">
            <label className="block text-[11px]">
              Room Code:
              <input
                type="text"
                value={roomCode}
                onChange={(e) => setRoomCode(e.target.value.toUpperCase())}
                className="win2k-field block w-full mt-0.5 px-1 py-0.5 text-base md:text-[12px] uppercase tracking-widest"
                maxLength={4}
                placeholder="XXXX"
              />
            </label>
            {errors?.code && (
              <p className="text-[11px] text-red-700">
                {errors.code[0]}
              </p>
            )}
            {errors?.room && (
              <p className="text-[11px] text-red-700">
                {errors.room[0]}
              </p>
            )}
            <Win2kButton
              type="submit"
              disabled={processing}
              className="w-full"
            >
              Join Room
            </Win2kButton>
          </form>

          <div className="flex items-center gap-2 text-[11px] text-[var(--win2k-button-text)]">
            <div className="flex-1 border-t border-[var(--win2k-shadow)]" />
            <span>or</span>
            <div className="flex-1 border-t border-[var(--win2k-shadow)]" />
          </div>

          <form onSubmit={handleCreate} className="flex justify-center">
            <Win2kButton
              type="submit"
              disabled={processing}
              className="text-[10px] px-3 py-0.5 min-h-0"
            >
              Create New Room
            </Win2kButton>
          </form>
        </div>
      </Win2kWindow>

      <RulesModal open={showRules} onClose={() => setShowRules(false)} />
    </div>
  );
}
